/**
 * Landing-page hero — auto-rotating tagline + subline across EN/HI/MR.
 *
 * Reads i18n keys `hero.tagline` and `hero.subline` for each locale (no
 * persistent language switch — uses i18n.t({lng: ...})). Pauses when the user
 * hovers / focuses any element inside the hero so the CTAs are never moving
 * targets.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRotatingLocale, localeText } from "@/hooks/useLocalePeek";

export default function Hero({ onRegister }) {
  const { t } = useTranslation();
  const { current, pause, resume } = useRotatingLocale(4500);

  const seeHow = () => {
    const el = document.getElementById("how-it-works");
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <section
      className="bg-white border-b border-slate-200"
      data-testid="hero"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24 text-center">
        {/* Live-exams chip — the only place specific exam names appear
            above the fold. Rotates locale in step with the H1. */}
        <p
          key={`chip-${current}`}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 animate-fade-in"
          data-testid="hero-live-chip"
          lang={current}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
          {localeText("hero.liveChip", current)}
        </p>
        <h1
          key={`tagline-${current}`}
          className="mt-4 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] animate-fade-in"
          data-testid="hero-tagline"
          lang={current}
        >
          {localeText("hero.tagline", current)}
        </h1>

        <p
          key={`subline-${current}`}
          className="mt-6 sm:mt-7 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto animate-fade-in"
          data-testid="hero-subline"
          lang={current}
        >
          {localeText("hero.subline", current)}
        </p>

        {/* Feb 2026 v3: no card, no bullets — the user asked for free
            impactful text only. Three short lines that read like a
            promise, each on its own row for scan-ability. */}
        <div className="mt-7 sm:mt-8 space-y-2 text-slate-700" data-testid="hero-impact-lines">
          <p className="text-[15px] sm:text-base font-semibold">
            Real cutoffs. Real bands. Real answers — in the time it takes to make chai.
          </p>
          <p className="text-[13.5px] sm:text-[15px] text-slate-500">
            One product. One-time fee. Every exam we&apos;re live for — none we&apos;re not.
          </p>
        </div>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
          <Button
            onClick={seeHow}
            className="h-11 px-6 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm"
            data-testid="hero-cta-primary"
          >
            {t("hero.ctaPrimary")}
          </Button>
          <Button
            onClick={onRegister}
            variant="outline"
            className="h-11 px-6 border-slate-300 text-slate-800 hover:bg-slate-50 font-semibold text-sm"
            data-testid="hero-cta-secondary"
          >
            {t("hero.ctaSecondary")}
          </Button>
          <Link
            to="/help/videos"
            className="h-11 px-4 sm:px-5 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-300 transition-colors"
            data-testid="hero-cta-video"
          >
            <PlayCircle className="w-4 h-4" />
            Watch 90-sec tour
          </Link>
        </div>

        <p
          className="mt-6 text-[12px] text-slate-500 leading-relaxed max-w-xl mx-auto"
          data-testid="hero-trust"
        >
          {t("hero.trust")}
        </p>
      </div>
    </section>
  );
}
